import React from "react";
import PropTypes from "prop-types";
import "../../components/ui-components/DataTable.css";

// Verdict → Tailwind colour classes (no external Badge needed — verdicts are
// custom strings not in the existing Badge status set).
const VERDICT_CLASSES = {
  ready: "bg-success-soft text-success",
  hold: "bg-danger-soft text-danger",
  insufficient: "bg-surface-2 text-text-2",
};

// on/off pill for current_value (1 = On, 0 = Off)
function OnOffPill({ value }) {
  return value === 1 ? (
    <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success select-none">
      On
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-surface-2 px-2 py-0.5 text-xs font-semibold text-text-2 select-none">
      Off
    </span>
  );
}

OnOffPill.propTypes = {
  value: PropTypes.number,
};

function VerdictBadge({ verdict }) {
  const cls = VERDICT_CLASSES[verdict] || VERDICT_CLASSES.insufficient;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold select-none ${cls}`}>
      {verdict || "—"}
    </span>
  );
}

VerdictBadge.propTypes = {
  verdict: PropTypes.string,
};

function MetricDetail({ metric }) {
  if (!metric || typeof metric !== "object") return null;
  const entries = Object.entries(metric);
  if (entries.length === 0) return null;
  return (
    <span className="ml-1 text-xs text-text-3">
      ({entries.map(([k, v]) => `${k}: ${v}`).join(", ")})
    </span>
  );
}

MetricDetail.propTypes = {
  metric: PropTypes.object,
};

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (_) {
    // silently ignore — clipboard may be unavailable in some envs
  }
}

// Spinner for async "running" state
function Spinner() {
  return (
    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" aria-hidden="true" />
  );
}

// FlagReadinessPanel is a PRESENTATIONAL component.
// Props:
//   flags    — array of flag verdict objects from the sync API
//   loading  — boolean
//   error    — Error | null
//   onCheck  — callback to trigger the sync readiness evaluation
//   ab       — model-flag A/B state: { status, flags, computedAt, error, onRun }
const FlagReadinessPanel = ({ flags, loading, error, onCheck, ab }) => {
  const safeFlags = flags || [];
  const safeAb = ab || { status: "idle", flags: [], onRun: () => {}, error: null };

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4 text-sm">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wide text-text-3">
            Flag Readiness
          </span>
          <p className="mt-0.5 text-xs text-text-3">
            Runs a competition against the current baseline. May take a moment.
          </p>
        </div>
        <button
          type="button"
          onClick={onCheck}
          disabled={loading}
          className="rounded-ctrl border border-border bg-surface-2 px-3 py-1 text-xs font-semibold text-text hover:bg-surface-2 disabled:opacity-50"
        >
          {loading ? "Checking…" : "Check readiness"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-3 rounded-ctrl border border-danger bg-danger-soft px-4 py-2 text-xs text-danger">
          Could not evaluate flag readiness: {error.message || String(error)}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="mb-3 space-y-2">
          <div className="h-8 animate-pulse rounded-ctrl bg-surface-2" />
          <div className="h-8 animate-pulse rounded-ctrl bg-surface-2" />
        </div>
      )}

      {/* Table */}
      {!loading && safeFlags.length > 0 && (
        <div className="overflow-x-auto rounded-ctrl border border-card-bd">
          <table className="data-table min-w-full text-sm">
            <thead>
              <tr>
                <th>Flag</th>
                <th>Current</th>
                <th>Verdict</th>
                <th>Reason</th>
                <th>Flip command</th>
              </tr>
            </thead>
            <tbody>
              {safeFlags.map((f) => (
                <tr key={f.key}>
                  <td className="font-medium text-text">{f.label}</td>
                  <td>
                    <OnOffPill value={f.current_value} />
                  </td>
                  <td>
                    <VerdictBadge verdict={f.verdict} />
                  </td>
                  <td className="text-text-2">
                    {f.reason}
                    <MetricDetail metric={f.metric} />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(f.flip_command)}
                      className="rounded-ctrl border border-border px-2 py-0.5 text-xs text-text-2 hover:text-text"
                      title={f.flip_command}
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state (no flags yet loaded) */}
      {!loading && safeFlags.length === 0 && !error && (
        <p className="mb-3 text-xs text-text-3">
          Click &ldquo;Check readiness&rdquo; to evaluate all shadow-gated flags.
        </p>
      )}

      {/* Model-flag A/B section */}
      <div className="mt-4 rounded-card border border-card-bd bg-surface-2 px-4 py-3">
        <div className="mb-2 flex items-center justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-text-3">
              Model-flag A/B
            </span>
            <p className="mt-0.5 text-xs text-text-3">
              Async competition runs evaluating the model flags. Slow — takes ~minutes.
            </p>
          </div>
          <button
            type="button"
            onClick={safeAb.onRun}
            disabled={safeAb.status === "running"}
            className="rounded-ctrl border border-border bg-surface-2 px-3 py-1 text-xs font-semibold text-text hover:bg-surface-2 disabled:opacity-50"
          >
            {safeAb.status === "running" ? "Running…" : safeAb.status === "done" ? "Re-run A/B" : "Run A/B (slow, ~minutes)"}
          </button>
        </div>

        {/* A/B error */}
        {safeAb.status === "failed" && safeAb.error && (
          <div className="mb-2 rounded-ctrl border border-danger bg-danger-soft px-4 py-2 text-xs text-danger">
            A/B evaluation failed: {typeof safeAb.error === "string" ? safeAb.error : (safeAb.error.message || String(safeAb.error))}
          </div>
        )}

        {/* Running state */}
        {safeAb.status === "running" && (
          <div className="flex items-center gap-2 py-2 text-xs text-text-3">
            <Spinner />
            <span>Running A/B competition — this may take several minutes…</span>
          </div>
        )}

        {/* Idle / not yet run */}
        {safeAb.status === "idle" && (
          <p className="text-xs text-text-3">
            Not yet run — click &ldquo;Run A/B&rdquo; to evaluate the model flags.
          </p>
        )}

        {/* Done — show results table */}
        {safeAb.status === "done" && safeAb.flags && safeAb.flags.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-ctrl border border-card-bd">
              <table className="data-table min-w-full text-sm">
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Verdict</th>
                    <th>Reason</th>
                    <th>Flip command</th>
                  </tr>
                </thead>
                <tbody>
                  {safeAb.flags.map((f) => (
                    <tr key={f.key}>
                      <td className="font-medium text-text">{f.label}</td>
                      <td>
                        <VerdictBadge verdict={f.verdict} />
                      </td>
                      <td className="text-text-2">
                        {f.reason}
                        <MetricDetail metric={f.metric} />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(f.flip_command)}
                          className="rounded-ctrl border border-border px-2 py-0.5 text-xs text-text-2 hover:text-text"
                          title={f.flip_command}
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {safeAb.computedAt && (
              <p className="mt-1 text-xs text-text-3">
                Computed at {new Date(safeAb.computedAt).toLocaleString()}
              </p>
            )}
          </>
        )}

        {/* Done but empty */}
        {safeAb.status === "done" && (!safeAb.flags || safeAb.flags.length === 0) && (
          <p className="text-xs text-text-3">No flags returned by the A/B run.</p>
        )}
      </div>

    </div>
  );
};

const abFlagShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  verdict: PropTypes.string,
  reason: PropTypes.string,
  metric: PropTypes.object,
  flip_command: PropTypes.string,
});

FlagReadinessPanel.propTypes = {
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      current_value: PropTypes.number,
      verdict: PropTypes.string,
      reason: PropTypes.string,
      metric: PropTypes.object,
      flip_command: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.object,
  onCheck: PropTypes.func.isRequired,
  ab: PropTypes.shape({
    status: PropTypes.oneOf(["idle", "running", "done", "failed"]),
    flags: PropTypes.arrayOf(abFlagShape),
    computedAt: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onRun: PropTypes.func.isRequired,
  }),
};

FlagReadinessPanel.defaultProps = {
  flags: [],
  loading: false,
  error: null,
  ab: { status: "idle", flags: [], onRun: () => {}, error: null },
};

export default FlagReadinessPanel;
